<script lang="ts" setup>
	import { useAuthStore } from "~/store/auth";
	const { $client } = useNuxtApp();
	const authStore = useAuthStore();
	const isUser = ref(!!authStore.user);
	const route = useRoute();
	watch(
		() => route.path,
		() => {
			document.activeElement.blur();
		},
	);
	const logout = async () => {
		try {
			const { success } = await $client.rpcAuth.logout.query();
			if (success) {
				authStore.logout();
				isUser.value = false;
				navigateTo("/tv");
			} else {
				console.error("Logout Error");
			}
		} catch (e) {
			console.error(e);
		}
	};
</script>

<template>
	<div
		class="flex flex-row h-screen overflow-scroll [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
	>
		<!-- Main -->
		<main class="flex flex-row h-full w-screen">
			<!-- Sidebar -->
			<div class="block relative !w-[20%]">
				<div
					class="fixed w-[inherit] inset-0 flex flex-col justify-start *:py-[2vh] *:mx-2 *:pr-12 *:rounded-2xl gap-y-2 py-[4vh] text-dark font-medium text-[2.5vh]"
				>
					<!-- Logo -->
					<NuxtLink
						class="flex justify-start items-center cursor-pointer"
						to="/tv"
					>
						<img
							alt="Logo"
							class="w-[10vh]"
							src="/images/multicolor_sedna_world_logo.svg"
						/>
					</NuxtLink>
					<NuxtLink
						class="flex justify-start items-center cursor-pointer group focus:bg-gray-200 focus:text-black focus:outline-none"
						:class="{
							'bg-primary text-white':
								route.path.includes('/tv/search'),
						}"
						to="/tv/search"
					>
						<div class="flex flex-row gap-[1vh] items-center h-fit">
							<div
								class="flex justify-center items-center w-[3vh] relative"
							>
								<img
									alt="search"
									class="w-[3vh] absolute flex justify-center items-center"
									:class="{
										'grayscale invert group-focus:grayscale-0 group-focus:invert-0':
											route.path.includes('/tv/search'),
									}"
									src="/images/tv_search.svg"
								/>
							</div>
							<div class="flex justify-center items-center">
								جستجو
							</div>
						</div>
					</NuxtLink>
					<NuxtLink
						class="flex justify-start items-center cursor-pointer group focus:bg-gray-200 focus:text-black focus:outline-none"
						:class="{
							'bg-primary text-white': route.path === '/tv',
						}"
						to="/tv"
					>
						<div class="flex flex-row gap-[1vh] items-center h-fit">
							<div
								class="flex justify-center items-center w-[3vh] relative"
							>
								<img
									alt="home"
									class="w-[3vh] absolute flex justify-center items-center"
									:class="{
										'grayscale invert group-focus:grayscale-0 group-focus:invert-0':
											route.path === '/tv',
									}"
									src="/images/tv_home.svg"
								/>
							</div>
							<div class="flex justify-center items-center">
								ویترین
							</div>
						</div>
					</NuxtLink>
					<NuxtLink
						class="flex justify-start items-center cursor-pointer group focus:bg-gray-200 focus:text-black focus:outline-none"
						:class="{
							'bg-primary text-white':
								route.path.includes('/tv/category'),
						}"
						to="/tv/category"
					>
						<div class="flex flex-row gap-[1vh] items-center h-fit">
							<div
								class="flex justify-center items-center w-[3vh] relative"
							>
								<img
									alt="category"
									class="w-[3vh] absolute flex justify-center items-center"
									:class="{
										'grayscale invert group-focus:grayscale-0 group-focus:invert-0':
											route.path.includes('/tv/category'),
									}"
									src="/images/tv_category.svg"
								/>
							</div>
							<div class="flex justify-center items-center">
								دسته‌بندی
							</div>
						</div>
					</NuxtLink>
					<NuxtLink
						class="flex justify-start items-center cursor-pointer group focus:bg-gray-200 focus:text-black focus:outline-none"
						:class="{
							'bg-primary text-white':
								route.path.includes('/tv/playlists'),
						}"
						to="/tv/playlists"
					>
						<div class="flex flex-row gap-[1vh] items-center h-fit">
							<div
								class="flex justify-center items-center w-[3vh] relative"
							>
								<img
									alt="lists"
									class="w-[3vh] absolute flex justify-center items-center"
									:class="{
										'grayscale invert group-focus:grayscale-0 group-focus:invert-0':
											route.path.includes(
												'/tv/playlists',
											),
									}"
									src="/images/tv_lists.svg"
								/>
							</div>
							<div class="flex justify-center items-center">
								گالری‌من
							</div>
						</div>
					</NuxtLink>
					<NuxtLink
						v-if="!isUser"
						class="flex justify-start items-center cursor-pointer focus:bg-gray-200 focus:outline-none"
						to="/tv/login"
					>
						<div class="flex flex-row gap-[1vh] items-center h-fit">
							<div
								class="flex justify-center items-center w-[3vh] relative"
							>
								<img
									alt="login"
									class="w-[3vh] absolute flex justify-center items-center -rotate-90"
									src="/images/tv_download.svg"
								/>
							</div>
							<div class="flex justify-center items-center">
								ورود
							</div>
						</div>
					</NuxtLink>
					<NuxtLink
						class="flex justify-start items-center cursor-pointer focus:bg-gray-200 focus:outline-none"
						to="/tv/accounts"
					>
						<div class="flex flex-row gap-[1vh] items-center h-fit">
							<div
								class="flex justify-center items-center w-[3vh] relative"
							>
								<img
									alt="swap"
									class="w-[3vh] absolute flex justify-center items-center"
									src="/images/tv_user_swap_panel.svg"
								/>
							</div>
							<div class="flex justify-center items-center">
								تغییر تماشا کننده
							</div>
						</div>
					</NuxtLink>
					<div
						v-if="isUser"
						class="flex flex-row gap-[1vh] cursor-pointer items-center h-fit text-errorRed focus:bg-gray-200 focus:outline-none"
						tabindex="0"
						@click="logout()"
					>
						<div
							class="flex justify-center items-center w-[3vh] relative"
						>
							<img
								alt="logout"
								class="w-[3vh] absolute flex justify-center items-center"
								src="/images/tv_logout.svg"
							/>
						</div>
						<div class="flex justify-center items-center">خروج</div>
					</div>
				</div>
			</div>
			<div class="w-[80%]">
				<slot></slot>
			</div>
		</main>
	</div>
</template>

<style></style>
